import { LoggedInUser } from '.';
import { SessionState } from './session';
import { StageState } from './stageState';

export interface BoardFullState extends BoardState {
  Session: SessionState;
  Stage: StageState;
}

export interface BoardState {
  board_list: Board[];
  active_board: Board | null;
  active_shared_board: boolean;
  shared_board_list: Board[];
  favorites: number[];
  labels: Label[];
  filter: number[];
}

export interface Favorite {
  id: number;
  updatedAt: string;
  owner: LoggedInUser;
  board: Board;
}

export interface Label {
  id: number;
  name: string;
  createdAt: string;
  updatedAt: string;
}

export interface Board {
  createdAt: string;
  id: number;
  name: string;
  owner: LoggedInUser;
  updatedAt: string;
  shareable: boolean;
  uuid: string;
  elements?: [];
  labels: Label[];
}

/* ACTIONS */

export enum BoardActionsKeys {
  LIST_BOARDS = 'LIST_BOARDS',
  LIST_SHARED_BOARDS = 'LIST_SHARED_BOARDS',
  LIST_FAVORITES = 'LIST_FAVORITES',
  LIST_LABELS = 'LIST_LABELS',
  APPLY_LABEL = 'APPLY_LABEL',
  CREATE_LABEL = 'CREATE_LABEL',
  EDIT_LABEL = 'EDIT_LABEL',
  DELETE_LABEL = 'DELETE_LABEL',
  SET_FAVORITE = 'SET_FAVORITE',
  SHARE_BOARD = 'SHARE_BOARD',
  UNSHARE_BOARD = 'UNSHARE_BOARD',
  ACCEPT_SHARED_BOARD = 'ACCEPT_SHARED_BOARD',
  REMOVE_SHARED_BOARD = 'REMOVE_SHARED_BOARD',
  SET_FILTER = 'SET_FILTER',
  OPEN_BOARD = 'OPEN_BOARD',
  DUPLICATE_BOARD = 'DUPLICATE_BOARD',
  RENAME_BOARD = 'RENAME_BOARD',
  CLOSE_BOARD = 'CLOSE_BOARD',
  CREATE_BOARD = 'CREATE_BOARD',
  DELETE_BOARD = 'DELETE_BOARD',
  RESET_STATE = 'RESET_STATE',
}

export interface BoardActionsPayload {
  [BoardActionsKeys.LIST_BOARDS]: void;
  [BoardActionsKeys.LIST_SHARED_BOARDS]: void;
  [BoardActionsKeys.LIST_FAVORITES]: void;
  [BoardActionsKeys.LIST_LABELS]: void;
  [BoardActionsKeys.CREATE_LABEL]: string;
  [BoardActionsKeys.EDIT_LABEL]: { labelId: number; name: string };
  [BoardActionsKeys.DELETE_LABEL]: number;
  [BoardActionsKeys.SET_FAVORITE]: number;
  [BoardActionsKeys.APPLY_LABEL]: { labelId: number; boardId: number; active?: boolean };
  [BoardActionsKeys.SHARE_BOARD]: number;
  [BoardActionsKeys.UNSHARE_BOARD]: number;
  [BoardActionsKeys.ACCEPT_SHARED_BOARD]: string;
  [BoardActionsKeys.REMOVE_SHARED_BOARD]: number;
  [BoardActionsKeys.SET_FILTER]: number | false;
  [BoardActionsKeys.OPEN_BOARD]: { id: number; shared?: boolean };
  [BoardActionsKeys.DUPLICATE_BOARD]: { boardId: number };
  [BoardActionsKeys.RENAME_BOARD]: { boardId: number; name: string };
  [BoardActionsKeys.CLOSE_BOARD]: void;
  [BoardActionsKeys.CREATE_BOARD]: void;
  [BoardActionsKeys.DELETE_BOARD]: { boardId: number };
  [BoardActionsKeys.RESET_STATE]: void;
}

/* MUTATIONS */

export enum BoardMutationsKeys {
  OPEN_BOARD = 'OPEN_BOARD',
  ADD_BOARD = 'ADD_BOARD',
  DELETE_BOARD = 'DELETE_BOARD',
  SET_FAVORITES = 'SET_FAVORITES',
  SET_LABELS = 'SET_LABELS',
  APPLY_LABEL = 'APPLY_LABEL',
  CREATE_LABEL = 'CREATE_LABEL',
  EDIT_LABEL = 'EDIT_LABEL',
  DELETE_LABEL = 'DELETE_LABEL',
  ADD_FAVORITE = 'ADD_FAVORITE',
  SHARE_BOARD = 'SHARE_BOARD',
  UNSHARE_BOARD = 'UNSHARE_BOARD',
  ACCEPT_SHARED_BOARD = 'ACCEPT_SHARED_BOARD',
  REMOVE_SHARED_BOARD = 'REMOVE_SHARED_BOARD',
  REMOVE_FAVORITE = 'REMOVE_FAVORITE',
  SET_FILTER = 'SET_FILTER',
  CLOSE_BOARD = 'CLOSE_BOARD',
  RENAME_BOARD = 'RENAME_BOARD',
  SET_BOARD_LIST = 'SET_BOARD_LIST',
  SET_SHARED_BOARD_LIST = 'SET_SHARED_BOARD_LIST',
  BOARD_RESET_STATE = 'BOARD_RESET_STATE',
}

export interface BoardMutationsPayload {
  [BoardMutationsKeys.OPEN_BOARD]: { board: Board; shared: boolean };
  [BoardMutationsKeys.ADD_BOARD]: Board;
  [BoardMutationsKeys.DELETE_BOARD]: { boardId: number };
  [BoardMutationsKeys.ADD_FAVORITE]: Favorite;
  [BoardMutationsKeys.SHARE_BOARD]: number;
  [BoardMutationsKeys.UNSHARE_BOARD]: number;
  [BoardMutationsKeys.ACCEPT_SHARED_BOARD]: Board;
  [BoardMutationsKeys.REMOVE_SHARED_BOARD]: number;
  [BoardMutationsKeys.SET_FILTER]: number;
  [BoardMutationsKeys.REMOVE_FAVORITE]: number;
  [BoardMutationsKeys.CLOSE_BOARD]: void;
  [BoardMutationsKeys.RENAME_BOARD]: { boardId: number; name: string };
  [BoardMutationsKeys.SET_BOARD_LIST]: BoardState['board_list'];
  [BoardMutationsKeys.SET_SHARED_BOARD_LIST]: BoardState['shared_board_list'];
  [BoardMutationsKeys.SET_FAVORITES]: BoardState['favorites'];
  [BoardMutationsKeys.SET_LABELS]: BoardState['labels'];
  [BoardMutationsKeys.CREATE_LABEL]: Label;
  [BoardMutationsKeys.EDIT_LABEL]: { labelId: number; name: string };
  [BoardMutationsKeys.DELETE_LABEL]: number;
  [BoardMutationsKeys.APPLY_LABEL]: { labelId: number; boardId: number; active?: boolean };
  [BoardMutationsKeys.BOARD_RESET_STATE]: void;
}

/* GETTERS */

export enum BoardGettersKeys {
  isBoardOpen = 'isBoardOpen',
  boardList = 'boardList',
  boardName = 'boardName',
  boardId = 'boardId',
  sharedBoard = 'sharedBoard',
  favorites = 'favorites',
  labels = 'labels',
  boardFilter = 'boardFilter',
  sharedBoardList = 'sharedBoardList',
}

export interface BoardGettersReturns {
  [BoardGettersKeys.isBoardOpen]: boolean;
  [BoardGettersKeys.boardName]: Board['name'];
  [BoardGettersKeys.boardId]: Board['id'];
  [BoardGettersKeys.sharedBoard]: boolean;
  [BoardGettersKeys.favorites]: BoardState['favorites'];
  [BoardGettersKeys.labels]: BoardState['labels'];
  [BoardGettersKeys.boardList]: BoardState['board_list'];
  [BoardGettersKeys.boardFilter]: BoardState['filter'];
  [BoardGettersKeys.sharedBoardList]: BoardState['shared_board_list'];
}
